import React, { useEffect, useRef } from 'react';

export default function PrivacyPolicyModal({ onClose, onAgree }) {
  const contentRef = useRef(null);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'auto'; };
  }, []);

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        
        {/* ─── HEADER ─── */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Privacy Policy</h2>
            <div style={styles.subtitle}>Effective Date: March 2026</div>
          </div>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* ─── SCROLLABLE CONTENT ─── */}
        <div style={styles.content} ref={contentRef}>
          
          <p style={styles.leadText}>
            At HealHub, we firmly believe that genuine healing — whether emotional, mental, or personal — can only occur in an environment grounded in absolute trust, respect, and unwavering protection of your inner world. Your thoughts, moods, emotions, private reflections, and personal growth journeys are profoundly personal and belong exclusively to you. We treat them as sacred.
          </p>
          
          <p style={styles.p}>
            This Privacy Policy is designed to provide a comprehensive, transparent, and detailed explanation of how HealHub (“HealHub,” “we,” “us,” or “our”) collects, processes, uses, discloses, secures, retains, and ultimately protects your personal data. It is drafted in alignment with Indian data protection laws, with a particular focus on the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> and the Digital Personal Data Protection Rules, 2025 (notified on November 13/14, 2025), the <strong>Information Technology Act, 2000 (IT Act)</strong> — especially Section 43A and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 (SPDI Rules, 2011) — the fundamental right to privacy as a Fundamental Right affirmed by the Supreme Court of India in <em>Justice K.S. Puttaswamy (Retd.) v. Union of India (2017)</em>, and the Information Technology (Indian Computer Emergency Response Team and Manner of Performing Functions and Duties) Rules, 2013 along with CERT-In Directions on cybersecurity incident reporting.
          </p>

          <p style={styles.p}>
            We operate as a Data Fiduciary under the DPDP Act. This imposes significant legal and ethical responsibilities on us to process your data lawfully, fairly, transparently, and only for specified, legitimate purposes. Our unwavering commitment is that your data will never be used for personalized advertising, behavioral tracking for commercial gain, selling or sharing with third-party marketers, insurance companies, data brokers, or any purpose that could exploit your vulnerabilities or emotions. We exist solely to support your self-care and mental wellness journey.
          </p>

          <div style={styles.divider} />

          <section style={styles.section}>
            <h3 style={styles.h3}><span style={styles.num}>1.</span> Our Identity and Role as Data Fiduciary</h3>
            <p style={styles.p}>
              HealHub is a digital platform dedicated to mental wellness and self-care, offering features such as AI-powered emotional support (Saksham AI), private journaling, mood tracking, Recovery Roadmaps, anonymous peer connections (Chattr), and public sharing spaces (Story Write).
            </p>
            <p style={styles.p}>
              As a Data Fiduciary, we determine the purposes and means of processing your personal data and bear full accountability for its protection. We implement privacy-by-design and privacy-by-default principles from the architecture stage, conduct periodic internal and external security audits, maintain reasonable security safeguards, and ensure ongoing compliance through regular reviews. We may be designated as a Significant Data Fiduciary by the Government of India depending on data volume and sensitivity, in which case we will fulfill additional obligations such as Data Protection Impact Assessments (DPIAs), independent audits, and appointment of a Data Protection Officer.
            </p>
            <p style={styles.p}>
              We also comply with broader corporate and operational requirements applicable to technology companies in India, including the Companies Act, 2013 (corporate governance, filings, and accounts), Goods and Services Tax Act, 2017, Consumer Protection Act, 2019, and relevant provisions of the Bharatiya Nyaya Sanhita, 2023 concerning data misuse or breach of trust.
            </p>
          </section>

          <section style={styles.section}>
            <h3 style={styles.h3}><span style={styles.num}>2.</span> What We Collect – Detailed Data Map</h3>
            <p style={styles.p}>
              We strictly adhere to <strong>data minimization</strong> — collecting only what is absolutely necessary — and <strong>purpose limitation</strong> — using data solely for the purposes disclosed at the time of collection.
            </p>
            
            <h4 style={styles.h4}>A. Personal Information (Identity Data)</h4>
            <ul style={styles.ul}>
              <li style={styles.li}><strong>Email address:</strong> Essential for account creation, identity verification, secure login, communication of important notices, and resetting credentials.</li>
              <li style={styles.li}><strong>Age or date of birth:</strong> Used to determine eligibility for services, apply age-appropriate safeguards, and manage consent requirements (especially for minors).</li>
              <li style={styles.li}><strong>Optional display name or pseudonym:</strong> You may choose a non-identifying name for public or community features to enhance privacy.</li>
            </ul>

            <div style={styles.calloutBox}>
              <h5 style={styles.calloutTitle}>For Users Aged 15–17 (Minors):</h5>
              <p style={{...styles.p, marginBottom: 0}}>
                Under Section 9 of the DPDP Act, individuals under the age of 18 are considered children. HealHub allows users aged 15 and above to access the platform. By creating an account, users aged 15–17 confirm that they have obtained consent from their parent or lawful guardian. We implement reasonable safeguards to protect minors and rely on user-declared information for age and consent. If we become aware that valid consent has not been obtained, we reserve the right to suspend or delete such accounts.
              </p>
            </div>

            <h4 style={styles.h4}>B. Sensitive Personal Data or Information (SPDI) & Health-Related Data</h4>
            <p style={styles.p}>Under the SPDI Rules, 2011, and reinforced by the DPDP Act, data relating to mental or emotional health receives the highest level of protection. This category includes:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Full conversations and transcripts with Saksham AI (emotional support interactions).</li>
              <li style={styles.li}>Private journal entries and reflections.</li>
              <li style={styles.li}>Recovery Roadmap assessments, progress trackers, and goal-setting data.</li>
              <li style={styles.li}>Mood tracking logs, emotional check-ins, self-reported wellness metrics, and related insights.</li>
            </ul>
            <p style={styles.p}>
              This data is inherently sensitive because it reveals intimate aspects of your mental and emotional state. It is collected only with explicit, informed consent for clearly defined purposes and is subject to enhanced encryption, strict access controls, and limited internal handling.
            </p>

            <h4 style={styles.h4}>C. Community, Usage, and Technical Data</h4>
            <ul style={styles.ul}>
              <li style={styles.li}><strong>Chattr interactions:</strong> Primarily pseudonymized or anonymized by design to promote emotional safety and free expression.</li>
              <li style={styles.li}><strong>Story Write posts:</strong> Only content you voluntarily choose to make public or share within chosen visibility settings.</li>
              <li style={styles.li}><strong>Limited technical and usage data:</strong> Such as device information, browser/app version, IP address (for security and fraud prevention), and session logs necessary for platform functionality, safety monitoring, and performance optimization.</li>
            </ul>
            <p style={styles.p}>
              We may generate and process fully anonymized or aggregated data for internal analytics, platform improvement, safety enhancements, or statistical research. We do not collect unnecessary categories such as precise geolocation (unless voluntarily provided), biometric data, or unrelated sensitive personal information. Financial information is processed only through secure third-party payment providers for specific services such as mentor payouts or user bookings.
            </p>
          </section>

          <section style={styles.section}>
            <h3 style={styles.h3}><span style={styles.num}>3.</span> Why We Process Your Data</h3>
            <p style={styles.p}>
              We process personal data only on the basis of valid consent — which must be free, specific, informed, unconditional, unambiguous, and demonstrated through a clear affirmative action — or other lawful grounds permitted under applicable law. Purposes include:
            </p>
            <ul style={styles.ul}>
              <li style={styles.li}>Delivering personalized emotional support through Saksham AI</li>
              <li style={styles.li}>Enabling journaling, mood tracking, and self-care tools</li>
              <li style={styles.li}>Facilitating peer connections in Chattr</li>
              <li style={styles.li}>Maintaining platform safety and integrity</li>
              <li style={styles.li}>Complying with legal obligations</li>
            </ul>
            <p style={styles.p}><strong>We do not process your data for personalized advertising, targeted marketing, or commercial profiling.</strong></p>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '36px' }}>
            <section>
              <h3 style={styles.h3}><span style={styles.num}>4.</span> Consent & Control</h3>
              <p style={styles.p}>Consent lies at the heart of our data practices. You may withdraw consent, delete data, or delete your account at any time. Withdrawal does not affect past lawful processing but may limit certain features.</p>
            </section>
            
            <section>
              <h3 style={styles.h3}><span style={styles.num}>5.</span> Your Rights</h3>
              <p style={styles.p}>You have the right to access, correct, update, or erase your data, lodge grievances, and exercise other rights under applicable law.</p>
              <p style={styles.p}><strong>Grievance Officer:</strong><br/><a href="mailto:healhubsupport@gmail.com" style={styles.link}>healhubsupport@gmail.com</a></p>
            </section>
          </div>

          <section style={styles.section}>
            <h3 style={styles.h3}><span style={styles.num}>6.</span> Special Protections for Saksham AI and Chattr</h3>
            <h4 style={styles.h4}>Saksham AI Conversations:</h4>
            <p style={styles.p}>
              These are treated as strictly private and confidential. Conversations are protected using strong encryption safeguards, including encryption in transit and at rest, and are used exclusively to deliver personalized support and insights.
            </p>
            <p style={styles.p}>
              We do not use them to train public or external AI models. However, to deliver certain features such as report generation and insights, limited data may be securely processed through trusted third-party AI providers (such as Grok or DeepSeek) acting under strict contractual obligations and prohibited from using your data for their own purposes.
            </p>
            
            <h4 style={styles.h4}>Chattr – Anonymous by Design:</h4>
            <p style={styles.p}>Chattr is designed to preserve anonymity and emotional safety. No real identity linking is exposed.</p>
          </section>

          <section style={styles.section}>
            <h3 style={styles.h3}><span style={styles.num}>7.</span> Data Security Measures & Breach Notification</h3>
            <p style={styles.p}>
              We implement reasonable security safeguards including encryption in transit and at rest, access controls, audits, and security testing.
            </p>
            <p style={styles.p}>
              In the event of a data breach, users will be notified and incidents will be reported to relevant authorities, including CERT-In, within applicable timelines as required by law.
            </p>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '36px' }}>
            <section>
              <h3 style={styles.h3}><span style={styles.num}>8.</span> Data Retention & Erasure</h3>
              <p style={styles.p}>Data is retained only as long as necessary. Inactive accounts may be deleted after 2 years. Users may request deletion at any time.</p>
            </section>
            <section>
              <h3 style={styles.h3}><span style={styles.num}>9.</span> Payments & Financial Data</h3>
              <p style={styles.p}>Payments for bookings or services are processed through trusted third-party providers such as Razorpay. HealHub does not store sensitive financial data such as card details or banking credentials. Limited transaction data may be retained for operational and compliance purposes.</p>
            </section>
          </div>

          <section style={styles.section}>
            <h3 style={styles.h3}><span style={styles.num}>10.</span> Third-Party Processors, Disclosures, & Transfers</h3>
            <p style={styles.p}>We may engage trusted processors under strict contracts. Data is shared only with consent or legal obligation.</p>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '16px' }}>
            <section>
              <h3 style={styles.h3}><span style={styles.num}>11.</span> Changes to This Policy</h3>
              <p style={styles.p}>We may update this policy periodically. Users will be notified of significant changes.</p>
            </section>
            <section>
              <h3 style={styles.h3}><span style={styles.num}>12.</span> Contact Us</h3>
              <p style={styles.p}>Email: <a href="mailto:healhubsupport@gmail.com" style={styles.link}>healhubsupport@gmail.com</a></p>
            </section>
          </div>

          <div style={styles.divider} />

          {/* ─── FINAL COMMITMENT & DISCLAIMER ─── */}
          <div style={styles.commitmentBox}>
            <div style={styles.commitmentIcon}>💖🌿</div>
            <h4 style={styles.commitmentTitle}>Final Commitment</h4>
            <p style={{...styles.p, marginBottom: 0}}>
              At HealHub, privacy is sacred. We do not exploit or monetize your emotional data.
            </p>
          </div>

          <div style={styles.disclaimerBox}>
            <h4 style={styles.disclaimerTitle}>Important Disclaimer</h4>
            <p style={styles.p}>
              This platform is not a substitute for medical or psychiatric care. In case of emergency, contact a professional.
            </p>
            <h4 style={{...styles.disclaimerTitle, marginTop: '16px'}}>Jurisdiction</h4>
            <p style={{...styles.p, marginBottom: 0, fontSize: '0.85rem'}}>
              This Privacy Policy is governed by the laws of India. Subject to applicable laws, the courts located in Delhi, India shall have jurisdiction.
            </p>
          </div>

        </div>

        {/* ─── FOOTER ACTION ─── */}
        <div style={styles.footer}>
          <button onClick={onAgree || onClose} style={styles.agreeBtn}>
            I Have Read and Agree
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PREMIUM STYLES ───
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 12, 10, 0.45)', // Deep dark mist
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '760px',
    height: '92vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    animation: 'modalSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
  },
  header: {
    padding: '24px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid #f0ecf9',
    zIndex: 2,
    flexShrink: 0
  },
  title: {
    margin: 0,
    fontFamily: '"Fraunces", serif',
    fontSize: '1.6rem',
    fontWeight: '700',
    color: '#1a1714', // Ink
    letterSpacing: '-0.02em'
  },
  subtitle: {
    fontSize: '0.8rem',
    color: '#9c9590', // Stone
    marginTop: '4px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  closeBtn: {
    background: '#f5f3f0', // Mist
    border: 'none',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#1a1714',
    transition: 'background 0.2s ease',
  },
  content: {
    padding: '32px',
    overflowY: 'auto',
    flex: 1,
    color: '#2d2926',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    scrollBehavior: 'smooth',
  },
  leadText: {
    fontSize: '1.15rem',
    lineHeight: '1.7',
    color: '#1a1714',
    fontWeight: '500',
    marginBottom: '20px',
    fontFamily: '"Fraunces", serif',
  },
  section: {
    marginBottom: '36px'
  },
  h3: {
    fontFamily: '"Fraunces", serif',
    fontSize: '1.25rem',
    color: '#1a1714',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px'
  },
  num: {
    color: '#c95b3d', // Terra
    fontSize: '1.1rem',
    fontWeight: '900'
  },
  h4: {
    fontSize: '0.95rem',
    fontWeight: '700',
    color: '#4a7c6f', // Sage
    marginTop: '20px',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  p: {
    fontSize: '0.95rem',
    lineHeight: '1.65',
    marginBottom: '14px',
    color: '#4a443f'
  },
  ul: {
    paddingLeft: '0',
    marginBottom: '20px',
    listStyle: 'none'
  },
  li: {
    position: 'relative',
    paddingLeft: '20px',
    marginBottom: '8px',
    fontSize: '0.95rem',
    lineHeight: '1.6',
    color: '#4a443f',
  },
  link: {
    color: '#c95b3d',
    textDecoration: 'none',
    fontWeight: '700'
  },
  divider: {
    height: '1px',
    backgroundColor: '#ede9e4',
    margin: '40px 0'
  },
  calloutBox: {
    backgroundColor: '#fdf4e1', // Amber-L
    border: '1px solid rgba(232, 161, 53, 0.3)',
    borderRadius: '16px',
    padding: '20px',
    margin: '24px 0'
  },
  calloutTitle: {
    fontSize: '0.9rem',
    fontWeight: '800',
    color: '#b07d1a',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '10px',
    marginTop: 0
  },
  commitmentBox: {
    backgroundColor: '#e4f0ed', // Sage-L
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    marginBottom: '24px'
  },
  commitmentIcon: {
    fontSize: '2rem',
    marginBottom: '8px'
  },
  commitmentTitle: {
    fontFamily: '"Fraunces", serif',
    fontSize: '1.3rem',
    color: '#2e6349',
    marginBottom: '8px',
    marginTop: 0
  },
  disclaimerBox: {
    backgroundColor: '#f9ede9', // Terra-L
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid rgba(201, 91, 61, 0.2)'
  },
  disclaimerTitle: {
    fontSize: '0.9rem',
    fontWeight: '800',
    color: '#a04530', // Terra-D
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
    marginTop: 0
  },
  footer: {
    padding: '20px 32px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    borderTop: '1px solid #f0ecf9',
    zIndex: 2,
    flexShrink: 0
  },
  agreeBtn: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#c95b3d', // Terra
    color: '#ffffff',
    border: 'none',
    borderRadius: '14px',
    fontSize: '1.05rem',
    fontWeight: '700',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(201, 91, 61, 0.25)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  }
};